import * as Sentry from '@sentry/react';

export function initializeSentry(): void {
  // Check if Sentry is configured via environment variables
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
  const release = import.meta.env.VITE_SENTRY_RELEASE || import.meta.env.VITE_APP_VERSION;
  
  // Only initialize Sentry if DSN is provided
  if (!dsn) {
    console.log('Sentry DSN not configured, error tracking disabled');
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release,
      
      // Performance Monitoring
      integrations: [
        // Browser tracing integration is included by default in newer versions
      ],
      
      // Performance Monitoring sample rate
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      
      // Session Replay
      replaysSessionSampleRate: environment === 'production' ? 0.01 : 0.1,
      replaysOnErrorSampleRate: 1.0,
      
      // Error filtering
      beforeSend(event, hint) {
        // Filter out development errors in production
        if (environment === 'production') {
          const error = hint.originalException;
          
          // Type guard for Error-like objects
          const isErrorLike = (e: unknown): e is { name?: string; message?: string } => {
            return typeof e === 'object' && e !== null;
          };
          
          // Skip chunk loading errors (usually not actionable)
          if (isErrorLike(error) && error.name === 'ChunkLoadError') {
            return null;
          }
          
          // Skip network errors from ad blockers, etc.
          if (
            isErrorLike(error) && (
              error.message?.includes('Failed to fetch') ||
              error.message?.includes('NetworkError')
            )
          ) {
            return null;
          }
        }
        
        return event;
      },
      
      // Additional configuration
      debug: environment === 'development',
      
      // User context
      initialScope: {
        tags: {
          component: 'web-app',
        },
      },
    });

    console.log(`Sentry initialized for ${environment} environment`);
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
}

// Helper function to capture Convex errors with additional context
export function captureConvexError(error: Error, context?: Record<string, unknown>): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  
  Sentry.captureException(error, {
    tags: {
      error_type: 'convex_error',
    },
    contexts: {
      convex: {
        ...context,
        timestamp: new Date().toISOString(),
      },
    },
  });
}

// Helper function to capture user feedback
export function captureUserFeedback(message: string, email?: string): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  
  // Use the newer feedback API
  Sentry.captureFeedback({
    name: email || 'Anonymous',
    email: email || 'anonymous@dn.place',
    message: message,
  });
}

// Helper to set user context
export function setUserContext(user: { id: string; email?: string; username?: string }): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
}

// Helper to add breadcrumb for debugging
export function addBreadcrumb(message: string, category: string = 'custom', level: 'info' | 'warning' | 'error' = 'info'): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    timestamp: Date.now() / 1000,
  });
}

// React Router imports for Sentry integration (if needed in the future)
// import React, { useEffect } from 'react';
// import {
//   useLocation,
//   useNavigationType,
//   createRoutesFromChildren,
//   matchRoutes,
// } from 'react-router-dom';
